import { useCallback, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { color } from '@/colors';
import { useHotkeyScope } from '@/hooks/useHotkeyScope';
import { Flex, FlexCenHor } from '@/UI/styles';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { TOOLBAR_WIDTH_V2 } from '../../consts/ui';
import { useNodeNullable } from '../../FlowContext';
import { DesignerEditor } from './Designer/DesignerEditor';
import { useEditorStore } from './editor.state';

// hack for nullable node id
const invalidNodeId = '';

export function Editor() {
  const cleanupRef = useRef<(() => Promise<void> | void) | null>(null);
  const nodeId = useEditorStore((selector) => selector.nodeId);
  const onClose = useEditorStore((selector) => selector.onClose);

  const node = useNodeNullable(nodeId ?? invalidNodeId);
  const { type } = node || {};

  const setCleanup = useCallback((cleanup: () => Promise<void> | void) => {
    cleanupRef.current = cleanup;
  }, []);

  const cleanUpAndClose = useCallback(() => {
    if (cleanupRef.current) {
      const result = cleanupRef.current();
      if (result instanceof Promise) {
        result.then(onClose).catch(onClose);
      } else {
        onClose();
      }
    } else {
      onClose();
    }
  }, [onClose]);

  useHotkeyScope('editor');

  useHotkeys('Escape', cleanUpAndClose, { scopes: 'editor' });

  return (
    <Flex data-testid="editor-container" sx={{ width: '100%', height: '100%', backgroundColor: color.Black100 }}>
      <FlexCenHor
        sx={{
          height: '100%',
          width: TOOLBAR_WIDTH_V2,
          backgroundColor: color.Black92,
          borderRight: `1px solid ${color.White08_T}`,
          pt: 2,
        }}
      >
        <AppXBtn size={24} onClick={cleanUpAndClose} />
      </FlexCenHor>
      {type === 'compv3' && nodeId && <DesignerEditor id={nodeId} setCleanup={setCleanup} />}
    </Flex>
  );
}
